import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  InputAdornment,
  useTheme,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import NumbersIcon from "@mui/icons-material/Numbers";
import LockIcon from "@mui/icons-material/Lock";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import ArrowBackIosNewOutlined from "@mui/icons-material/ArrowBackIosNewOutlined";

import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseapi } from "../Environmentalurl";
import useIPAddress from "../Utils/useIPAddress";
import { entity_id, getGeolocation, location, Platform } from "../Utils/Const";
import DashboardHeader from "../Dashboard/DashboardHeader";
import api from "../Utils/Api";

export default function UpdateBankDetails() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const ip = useIPAddress();

  const [form, setForm] = useState({
    bank: "",
    accountNumber: "",
    confirmAccountNumber: "",
    ifsc: "",
    name: "",
    mobileno: "",
  });

  const [bankOptions, setBankOptions] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  // Fetch session data + build bank options from localStorage BankMaster
  useEffect(() => {
    const bank = JSON.parse(sessionStorage.getItem("PDBankName") || "''");
    const name = JSON.parse(sessionStorage.getItem("NameOnCard") || "''");
    const accountNumber = JSON.parse(
      sessionStorage.getItem("PDBankAccountNo") || "''",
    );
    const ifsc = JSON.parse(sessionStorage.getItem("PDBankIFSC") || "''");
    const mobileData = JSON.parse(
      sessionStorage.getItem("mobileNumber") || "''",
    );

    setForm({
      bank,
      name,
      accountNumber,
      confirmAccountNumber: accountNumber,
      ifsc,
      mobileno: mobileData,
    });

    // ── Read BankMaster from localStorage ──────────────────────────────────
    try {
      const raw = localStorage.getItem("BankMaster");
      if (raw) {
        const parsed = JSON.parse(raw); // array of bank objects
        const options = parsed.map((b) => ({
          label: b.BankName,
          value: b.BankName,
          ifsc: b.IFSC || "", // carry IFSC so we can auto-fill it
        }));
        setBankOptions(options);
      }
    } catch (err) {
      console.error("Error reading BankMaster from localStorage:", err);
    }
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // When a bank is selected from the dropdown, also auto-fill its IFSC
  const handleBankChange = (_, newVal) => {
    if (newVal) {
      setForm((prev) => ({
        ...prev,
        bank: newVal.value,
        ifsc: newVal.ifsc || prev.ifsc, // auto-fill IFSC if available
      }));
    } else {
      setForm((prev) => ({ ...prev, bank: "" }));
    }
  };

  // Submit
  const handleSubmit = async () => {
    if (form.accountNumber !== form.confirmAccountNumber) {
      alert("Account numbers do not match.");
      return;
    }

    const mobileNumber = JSON.parse(
      sessionStorage.getItem("mobileNumber") || "''",
    );

    const payload = {
      ReqType: "MT_User_UpdateBankDetails",
      Statement: "Account",
      Mobilenumber: mobileNumber,
      BankAccountNo: form.accountNumber,
      BankAccountHolderName: form.name,
      BankName: form.bank,
      BankIFSC: form.ifsc,
      WalletMobile: "",
      Platform,
      EntityID: entity_id,
      GeoLocation: location,
      MachineID: ip,
    };

    try {
      const res = await api.post(`${baseapi}/updatedetails`, payload);
      setPopupMessage(res.data.Message || "Something happened");
      setPopupOpen(true);
    } catch (err) {
      console.error("Error updating bank details:", err);
      alert("Please try again later.");
    }
  };

  return (
    <>
      {/* HEADER */}
      <DashboardHeader />

      {/* MAIN BODY */}
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          p: { xs: 2, md: 5 },
          backgroundColor: isDark ? "#0a122a" : "#f6f6f6",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            maxWidth: "750px",
            mx: "auto",
            p: { xs: 3, md: 5 },
            borderRadius: "20px",
            backgroundColor: isDark ? "#1c2536" : "#ffffff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} mb={4}>
            <IconButton
              onClick={() => navigate("/profile-settings")}
              sx={{ bgcolor: "#fff", color: "#1e5fd8" }}
            >
              <ArrowBackIosNewOutlined />
            </IconButton>
            <Typography variant="h5" textAlign="center" fontWeight="bold">
              Update Bank Details
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* BANK NAME — Autocomplete from localStorage */}
            <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
              <Typography sx={{ mb: 1, fontSize: "13px", opacity: 0.8 }}>
                Bank Name
              </Typography>
              <Autocomplete
                options={bankOptions}
                value={bankOptions.find((b) => b.value === form.bank) || null}
                onChange={handleBankChange}
                isOptionEqualToValue={(option, val) =>
                  option.value === val.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select or type bank name"
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* ACCOUNT HOLDER NAME */}
            <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
              <Typography sx={{ mb: 1, fontSize: "13px", opacity: 0.8 }}>
                Account Holder Name
              </Typography>
              <TextField
                fullWidth
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* ACCOUNT NUMBER */}
            <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
              <Typography sx={{ mb: 1, fontSize: "13px", opacity: 0.8 }}>
                Account Number
              </Typography>
              <TextField
                fullWidth
                name="accountNumber"
                autoComplete="off"
                value={form.accountNumber}
                onChange={(e) =>
                  setForm({
                    ...form,
                    accountNumber: e.target.value.replace(/\D/g, ""),
                  })
                }
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                placeholder="Enter account number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NumbersIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* CONFIRM ACCOUNT NUMBER */}
            <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
              <Typography sx={{ mb: 1, fontSize: "13px", opacity: 0.8 }}>
                Confirm Account Number
              </Typography>
              <TextField
                fullWidth
                name="confirmAccountNumber"
                value={form.confirmAccountNumber}
                onChange={handleChange}
                placeholder="Re-enter account number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* IFSC */}
            <Grid size={{ xs: 12 }} item xs={12}>
              <Typography sx={{ mb: 1, fontSize: "13px", opacity: 0.8 }}>
                IFSC Code
              </Typography>
              <TextField
                fullWidth
                name="ifsc"
                value={form.ifsc}
                onChange={handleChange}
                placeholder="Enter IFSC"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CorporateFareIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* BUTTON */}
            <Grid size={{ xs: 12 }} item xs={12} textAlign="center" mt={2}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  px: 6,
                  py: 1.3,
                  borderRadius: "10px",
                  fontWeight: 600,
                  background: "linear-gradient(90deg, #00c853, #70ff70)",
                  color: "#000",
                }}
              >
                UPDATE
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* POPUP */}
        <Dialog open={popupOpen} onClose={() => setPopupOpen(false)}>
          <DialogContent>
            <Typography>{popupMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPopupOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
